import dayjs from 'dayjs'
import { Exchange } from '../proto/generated/common'
import { ExchangeTradingSession, TradingSession } from '../proto/generated/trading-calendar'

/**
 * On a session calendar day, true while `atMs` is strictly before that session's regular open (`startTime`).
 * Used to disable intraday-style views until the cash session has opened (e.g. before 9:15 IST for NSE).
 */
export function getBeforeRegularSessionOpenInfo(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    exchange: Exchange | undefined,
    atMs: number = Date.now()
): { beforeRegularOpen: boolean; session?: TradingSession } {
    if (!exchangeTradingSessions?.length || exchange === undefined) {
        return { beforeRegularOpen: false }
    }
    const sess = exchangeTradingSessions.find((s) => s.exchange === exchange)
    if (!sess?.tradingSessions?.length) {
        return { beforeRegularOpen: false }
    }
    for (const ts of sess.tradingSessions) {
        if (!dayjs(ts.startTime).isSame(dayjs(atMs), 'day')) {
            continue
        }
        if (atMs < ts.startTime) {
            return { beforeRegularOpen: true, session: ts }
        }
    }
    return { beforeRegularOpen: false }
}

/** True during pre-open, regular, or post-close window for the symbol's exchange (streaming-eligible hours). */
export function isWithinChartLiveTradingHours(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    exchange: Exchange | undefined,
    atMs: number = Date.now()
): boolean {
    if (!exchangeTradingSessions?.length || exchange === undefined) return false
    const sess = exchangeTradingSessions.find((s) => s.exchange === exchange)
    if (!sess?.tradingSessions?.length) return false

    for (const ts of sess.tradingSessions) {
        if (atMs >= ts.preOpenStartTime && atMs <= ts.preOpenEndTime) return true
        if (atMs >= ts.startTime && atMs <= ts.endTime) return true
        if (atMs >= ts.postCloseStartTime && atMs <= ts.postCloseEndTime) return true
    }
    return false
}

/** Epoch ms boundaries after `now` that can change live vs not-live for this exchange. */
export function getFutureTradingSessionBoundaries(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    exchange: Exchange | undefined,
    nowMs: number = Date.now()
): number[] {
    if (!exchangeTradingSessions?.length || exchange === undefined) return []
    const sess = exchangeTradingSessions.find((s) => s.exchange === exchange)
    if (!sess?.tradingSessions?.length) return []

    const out: number[] = []
    for (const ts of sess.tradingSessions) {
        for (const t of [
            ts.preOpenStartTime,
            ts.preOpenEndTime,
            ts.startTime,
            ts.endTime,
            ts.postCloseStartTime,
            ts.postCloseEndTime
        ]) {
            if (t > nowMs) out.push(t)
        }
    }
    return out
}
